import { formatDateTime } from '../formatDate';

export type InventoryRequestApiStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface InventoryRequestRow {
  id: string;
  requestCode: string;
  source: string;
  sourceLocationId: number | null;
  destination: string;
  destinationLocationId: number | null;
  itemCount: number;
  date: string;
  /** Raw ISO for client sort (display uses `date`). */
  createdAt: string;
  status: InventoryRequestApiStatus;
  statusLabel: string;
  createdBy?: string;
}

export interface RequestDetailItem {
  id: number;
  warehouseInventoryProductId: number | null;
  productCode: string;
  productName: string;
  productType: string;
  status: string;
  rejectionReason?: string | null;
}

export interface RequestDetail extends InventoryRequestRow {
  notes?: string | null;
  createdBy: string;
  resolvedAt?: string | null;
  /** Present when the request was approved or rejected by a user. */
  resolvedBy?: string | null;
  transferCode?: string | null;
  transferId?: number | null;
  items: RequestDetailItem[];
}

export interface RequestFilterState {
  requestCode: string;
  sourceLocation: string;
  destinationLocation: string;
  dateFrom: string;
  dateTo: string;
  status: string;
}

export interface RequestPaginationState {
  total: number;
  currentPage: number;
  itemsPerPage: number;
  totalPages: number;
}

export const mapApiStatusToLabel = (status: string): string => {
  if (status === 'PENDING') return 'Pendiente';
  if (status === 'APPROVED') return 'Aprobada';
  if (status === 'REJECTED') return 'Rechazada';
  return status;
};

export const mapUiStatusToApiStatus = (status: string): InventoryRequestApiStatus | undefined => {
  if (status === 'Pendiente') return 'PENDING';
  if (status === 'Aprobada') return 'APPROVED';
  if (status === 'Rechazada') return 'REJECTED';
  return undefined;
};

function formatUserName(user?: { firstName?: string; lastName?: string }): string | undefined {
  if (user == null) return undefined;
  const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  return name !== '' ? name : undefined;
}

function parseLocationId(id: unknown): number | null {
  if (id == null || id === '') {
    return null;
  }
  const parsed = Number(id);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function mapApiRequestToRow(request: Record<string, unknown>): InventoryRequestRow {
  const sourceLocation = request.sourceLocation as { id?: number; name?: string } | undefined;
  const destinationLocation = request.destinationLocation as
    | { id?: number; name?: string }
    | undefined;
  const apiStatus = (request.status as InventoryRequestApiStatus) ?? 'PENDING';

  return {
    id: String(request.id ?? ''),
    requestCode: (request.requestCode as string) ?? `SOL-${request.id}`,
    source: sourceLocation?.name ?? '—',
    sourceLocationId: parseLocationId(sourceLocation?.id),
    destination: destinationLocation?.name ?? '—',
    destinationLocationId: parseLocationId(destinationLocation?.id),
    itemCount: Number((request as { itemCount?: number }).itemCount ?? 0),
    date: formatDateTime((request.createdAt as string) ?? '') ?? '—',
    createdAt: (request.createdAt as string) ?? '',
    status: apiStatus,
    statusLabel: mapApiStatusToLabel(apiStatus),
    createdBy: formatUserName(
      request.createdBy as { firstName?: string; lastName?: string } | undefined,
    ),
  };
}

export function mapApiRequestToDetail(request: Record<string, unknown>): RequestDetail {
  const row = mapApiRequestToRow(request);
  const inventoryTransfer = request.inventoryTransfer as
    | { id?: number; transferCode?: string }
    | null
    | undefined;
  const itemsRaw = (request.items as Array<Record<string, unknown>>) ?? [];

  const items: RequestDetailItem[] = itemsRaw.map((item) => {
    const wip = item.warehouseInventoryProduct as Record<string, unknown> | undefined;
    const product = wip?.product as { name?: string; category?: { name?: string } } | undefined;
    const wipIdRaw = wip?.id != null ? Number(wip.id) : NaN;

    return {
      id: Number(item.id),
      warehouseInventoryProductId: Number.isFinite(wipIdRaw) && wipIdRaw > 0 ? wipIdRaw : null,
      productCode: (wip?.productCodeUnique as string) ?? '—',
      productName: product?.name ?? '—',
      productType: product?.category?.name ?? '—',
      status: mapApiStatusToLabel((item.status as string) ?? 'PENDING'),
      rejectionReason: (item.rejectionReason as string | null) ?? null,
    };
  });

  const createdByUser = request.createdBy as { firstName?: string; lastName?: string } | undefined;
  const resolvedByUser = request.resolvedBy as
    | { firstName?: string; lastName?: string }
    | undefined
    | null;

  return {
    ...row,
    notes: (request.notes as string | null) ?? null,
    createdBy: formatUserName(createdByUser) ?? '—',
    resolvedAt: request.resolvedAt ? (formatDateTime(request.resolvedAt as string) ?? null) : null,
    resolvedBy: formatUserName(resolvedByUser ?? undefined) ?? null,
    transferCode: inventoryTransfer?.transferCode ?? null,
    transferId: inventoryTransfer?.id != null ? Number(inventoryTransfer.id) : null,
    items,
  };
}
